import React from 'react';
import styled from 'styled-components';
import { Skeleton } from 'antd';

import useCurrentCourse from '@src/hooks/useCurrentCourse';
import { isPublished, isFundraisingRefund, isDiscontinued } from '@src/utils/course';
import { STATE_SUCCESS } from '@src/const/common';

import CourseInfo from './courseInfo';
import Interaction from './interaction';
import CoursePrice from './coursePrice';
import Progress from './progress';

const CourseDetailWrapper = styled.div`
	padding-bottom: ${props => (props.showCourseStatus ? '16px' : 0)};
	border-bottom: ${props => (props.showCourseStatus ? '1px solid #F0F0F0' : 'none')};

	@media ${props => props.theme.device.tablet} {
		display: ${props => (props.showProgress ? 'flex' : 'grid')};
		flex-direction: column;
		align-items: ${props => (props.isFundraisingRefund ? 'center' : 'initial')};
		grid-column-gap: 4px;
	}

	@media ${props => props.theme.device.smTablet} {
		display: ${props => (props.showProgress ? 'flex' : 'block')};
		flex-direction: column;
		align-items: center;
	}
`;

const InteractionWrapper = styled(Interaction)`
	margin-top: 16px;

	@media ${props => props.theme.device.tablet} {
		display: none;
	}
`;

const FixBottom = styled.div`
	width: 100vw;
	position: fixed;
	bottom: 0;
	left: 0;
	display: flex;
	justify-content: flex-end;
	padding: 8px 20px;
	background-color: #ffffff;
	box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
	z-index: 98;

	@media ${props => props.theme.device.lgDesktop} {
		width: 100%;
		position: relative;
		padding: 0;
		margin-top: 16px;
		box-shadow: none;
	}

	@media ${props => props.theme.device.desktop} {
		width: 100%;
		position: relative;
		padding: 0;
		margin-top: 16px;
		box-shadow: none;
	}

	/**
	 * Make footer padding top and bottom '18px' to override common footer.
	 * Based on figma, we should remove it after new common footer done.
	 */
	@media ${props => props.theme.device.tablet} {
		padding: 18px 12px;
	}

	/**
	 * Make footer padding top and bottom '18px' to override common footer.
	 * Based on figma, we should restore to 'padding: 12px' after new common footer done.
	 */
	@media ${props => props.theme.device.mobile} {
		padding: 18px 12px;
	}
`;

const FixInteraction = styled(Interaction)`
	display: none;

	@media ${props => props.theme.device.tablet} {
		display: flex;
		margin-right: 64px;
	}
`;

interface ICourseDetail {
	showCourseStatus: boolean;
}

/**
 * Note:
 * This component must be refactored,
 * because it uses conditions instead of state to determine the style,
 * so the structure is too complicated to maintain.
*/
const CourseDetail = ({ showCourseStatus }: ICourseDetail): JSX.Element => {
	const { currentCourseState, currentCourse: { status, paid }, isPreview } = useCurrentCourse();
	const showInteraction = paid && (isPublished(status) || isDiscontinued(status));
	const showProgress = !isPublished(status) && !isDiscontinued(status);

	if (currentCourseState !== STATE_SUCCESS) {
		return (
			<Skeleton active />
		);
	}
	return (
		<CourseDetailWrapper
			isFundraisingRefund={isFundraisingRefund(status)}
			showCourseStatus={showCourseStatus}
			showProgress={showProgress}
		>
			{(isPublished(status) || isDiscontinued(status) || isPreview) && (<CourseInfo />)}
			{showInteraction && !isPreview && <InteractionWrapper />}
			{showProgress && !isPreview && <Progress />}
			{!isFundraisingRefund(status) && (
				<FixBottom>
					{showInteraction && <FixInteraction />}
					<CoursePrice />
				</FixBottom>
			)}
		</CourseDetailWrapper>
	);
};

export default CourseDetail;
